import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError, API_BASE, authHeaders } from "@/lib/api";
import { Download, Trash2, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ENQUIRY_STATUS = ["New", "Reviewed", "Contacted", "Closed"];

const STATUS_STYLES = {
  New: "bg-execution text-white border-execution",
  Reviewed: "bg-[#00C779] text-white border-[#00C779]",
  Contacted: "bg-obsidian text-white border-obsidian",
  Closed: "bg-muted text-muted-foreground border-border",
};

const STATUS_LABEL = {
  New: "new",
  Reviewed: "reviewed",
  Contacted: "contacted",
  Closed: "closed",
};

export default function AdminEnquiries() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/enquiries", { params });
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const exportCsv = () => {
    fetch(`${API_BASE}/enquiries/export`, { headers: authHeaders() })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "nexra_enquiries.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) { alert(formatApiError(e)); }
  };

  const counts = ENQUIRY_STATUS.reduce((acc, s) => {
    acc[s] = items.filter((e) => (e.status || "New") === s).length;
    return acc;
  }, {});

  return (
    <AdminLayout
      title="Client enquiries"
      subtitle={`${items.length} submission${items.length === 1 ? "" : "s"} — track status from new → contacted → in discussion → closed`}
      actions={
        <button onClick={exportCsv} className="btn-secondary" data-testid="enquiries-export-csv">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      }
    >
      {/* Status summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="enquiries-status-summary">
        {ENQUIRY_STATUS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
            className={`border p-4 text-left transition-colors ${statusFilter === s ? "border-obsidian bg-obsidian text-white" : "border-border bg-white hover:border-obsidian"}`}
            data-testid={`enquiries-status-card-${s}`}
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-70 mb-2">{STATUS_LABEL[s]}</div>
            <div className="font-heading text-3xl font-bold tracking-tighter">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="border border-border bg-white p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-9 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input-swiss pl-10"
              placeholder="Search by name, organisation, email, description..."
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="enquiries-search"
            />
          </div>
          <select className="input-swiss md:col-span-3" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="enquiries-status-filter">
            <option value="">All statuses</option>
            {ENQUIRY_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}

      <div className="border border-border bg-white overflow-x-auto" data-testid="enquiries-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-obsidian text-white text-left">
            <tr>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Status</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Organisation</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Contact</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Summary</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Submitted</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="py-16 text-center text-muted-foreground">No enquiries match your filters.</td></tr>
            ) : items.map((e) => {
              const st = e.status || "New";
              return (
                <tr key={e.id} className="hover:bg-muted cursor-pointer" onClick={() => setSelected(e)} data-testid={`enquiry-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${STATUS_STYLES[st]}`}>
                      {STATUS_LABEL[st]}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{e.organisation}</td>
                  <td className="px-4 py-3 text-sm">
                    <div>{e.name}</div>
                    <div className="text-xs text-muted-foreground">{e.email}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground line-clamp-2 max-w-md">{e.description}</td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{new Date(e.submitted_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                    {user?.role === "admin" && (
                      <button
                        onClick={() => del(e.id)}
                        className="h-8 w-8 inline-flex items-center justify-center border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                        data-testid={`enquiry-delete-${e.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <EnquiryDrawer
          enquiry={selected}
          onClose={() => setSelected(null)}
          onUpdated={(u) => {
            setSelected(u);
            setItems((xs) => xs.map((x) => (x.id === u.id ? u : x)));
          }}
        />
      )}
    </AdminLayout>
  );
}

function EnquiryDrawer({ enquiry, onClose, onUpdated }) {
  const [status, setStatus] = useState(enquiry.status || "New");
  const [notes, setNotes] = useState(enquiry.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/enquiries/${enquiry.id}`, { status, notes });
      onUpdated(data);
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-obsidian/40 backdrop-blur-sm flex justify-end" data-testid="enquiry-drawer">
      <aside className="w-full max-w-xl bg-white h-full overflow-y-auto border-l border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div className="overline">Enquiry</div>
          <button onClick={onClose} className="p-2 hover:bg-muted" data-testid="enquiry-drawer-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <h2 className="font-heading text-3xl font-bold tracking-tight">{enquiry.organisation}</h2>
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${STATUS_STYLES[status]}`}>
              {STATUS_LABEL[status]}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Name" value={enquiry.name} />
            <Info label="Email" value={enquiry.email} />
            <Info label="Phone" value={enquiry.phone} />
            <Info label="Submitted" value={new Date(enquiry.submitted_at).toLocaleString()} />
          </div>
          <div>
            <div className="overline mb-2">Delivery situation</div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed border border-border p-4 bg-muted">
              {enquiry.description}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Status</span>
              <select className="input-swiss" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="enquiry-status-select">
                {ENQUIRY_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Internal notes</span>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              className="input-swiss min-h-[120px]"
              placeholder="Call notes, decisions, next actions..."
              data-testid="enquiry-notes"
            />
          </label>

          <div className="pt-4 border-t border-border flex items-center justify-between">
            <a href={`mailto:${enquiry.email}`} className="btn-secondary" data-testid="enquiry-reply">
              Reply via email
            </a>
            <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="enquiry-save">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
